import actionCreator from '../../shared/decorators/actionCreator';
import { REDUX_NAMESPACE } from './constants';
import { isNil, isNotNil } from '../../../util/object';
import { isNotNumber } from '../../../util/numbers';
import recordForUndo from '../../shared/decorators/recordForUndo';

const ACTION_CREATORS = {
  /**
   * Actions
   */

  /**
   * Add a cached URL value to an associated frame number
   * @param frameNumber
   * @param frameUrl
   * @returns {{frameUrl: *, frameNumber: *}}
   */
  @actionCreator(REDUX_NAMESPACE)
  addFrameThumbnailUrl(frameNumber, frameUrl) {
    return {
      frameNumber,
      frameUrl,
    };
  },

  /**
   * Cancel media trimming
   */
  @actionCreator(REDUX_NAMESPACE)
  closeMediaTrimmer() {},

  /**
   * Extract a thumbnail for the trimmer timeline
   *
   * @param {Number} frameNumber - the frame number we want to extract
   */
  @actionCreator(REDUX_NAMESPACE)
  extractTimelineThumbnail(frameNumber) {
    return {
      frameNumber,
    };
  },

  /**
   * Finish media trimming - closes modal and then sets values back in creative state.
   *
   * @param {boolean} trimCreative - set to true if after the trim action we want to set the creative to match
   * @param {boolean} publishCreative - set to true if after the trim action we also want to publish the creative
   */
  @actionCreator(REDUX_NAMESPACE)
  @recordForUndo()
  finishMediaTrimming(trimCreative, publishCreative) {
    return {
      trimCreative,
      publishCreative,
    };
  },

  /**
   * Called by the finishMediaTrimming action, this finalizes the data back to the creative state.
   * @param {String} contentId - ID of content being updated
   * @param (Number} start - start time in seconds for content clip relative to media
   * @param {Number} end - end time in seconds for the content clip relative to media
   */
  @actionCreator(REDUX_NAMESPACE)
  saveTrimInfoToCreative(contentId, start, end) {
    if (isNil(contentId)) {
      throw new Error('Cannot save info with nil content ID');
    }

    return {
      contentId,
      end,
      start,
    };
  },

  /**
   * Open the media trimmer modal with the given content ID. Ideally is a video or audio content ID.
   * If not an error may occur elsewhere.
   *
   * @param {String} contentId
   * @param {boolean} shouldTrimCreative - if true, trimmer will also trim length of entire creative on finish.
   * @param {boolean} shouldPublishCreative - if true, trimmer will trigger a publish action on finish.
   * @returns {{contentId: string, shouldPublishCreative: boolean, shouldTrimCreative: boolean}}
   */
  @actionCreator(REDUX_NAMESPACE)
  openMediaTrimmer(
    contentId,
    shouldTrimCreative = false,
    shouldPublishCreative = false,
  ) {
    if (isNil(contentId)) {
      throw new Error('Cannot open media trimmer with null content ID');
    }

    return {
      contentId,
      shouldPublishCreative,
      shouldTrimCreative,
    };
  },

  /**
   * Play the preview media
   */
  @actionCreator(REDUX_NAMESPACE)
  playMedia() {},

  /**
   * Pause the preview media
   */
  @actionCreator(REDUX_NAMESPACE)
  pauseMedia() {},

  /**
   * Mute the preview media
   */
  @actionCreator(REDUX_NAMESPACE)
  muteMedia() {},

  /**
   * Unmute the preview media
   */
  @actionCreator(REDUX_NAMESPACE)
  unmuteMedia() {},

  /**
   * Zoom in on trimmer timeline
   *
   * @param {Number} zoomStart - the starting time on the timeline for the zoom
   * @param {Number} zoomEnd -  ths ending time for the zoom
   */
  @actionCreator(REDUX_NAMESPACE)
  zoomIn(zoomStart, zoomEnd) {
    return {
      zoomEnd,
      zoomStart,
    };
  },

  /**
   * Zoom out on trimmer timeline
   */
  @actionCreator(REDUX_NAMESPACE)
  zoomOut() {},

  /**
   * Set isTrimming which indicates if a user is currently dragging to trim
   *
   * @param {boolean} isTrimming
   * @returns {{isTrimming: boolean}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setIsTrimming(isTrimming) {
    return {
      isTrimming,
    };
  },

  /**
   * Set mainMediaLoaded to true or false, depending on state of media loading
   *
   * @param {boolean} isLoaded
   * @returns {{isLoaded: boolean}}
   */
  @actionCreator(REDUX_NAMESPACE)
  setMainMediaLoaded(isLoaded) {
    return {
      isLoaded,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  updateClipStartEnd(currentTime, start, end) {
    return {
      currentTime,
      end,
      start,
    };
  },

  /**
   * Update the time value of the scrubber as it relates to previewing the media
   *
   * @param {Number} previewTime
   * @returns {{previewTime: Number}}
   */
  @actionCreator(REDUX_NAMESPACE)
  updatePreviewScrubberTime(previewTime) {
    if (isNotNil(previewTime) && isNotNumber(previewTime)) {
      throw new Error(
        'Cannot update preview time with non-number or non nil value',
      );
    }

    return {
      previewTime,
    };
  },

  /**
   * Update the time value of the scrubber as it relates to user interaction
   *
   * @param {Number} time
   * @returns {{time: Number}}
   */
  @actionCreator(REDUX_NAMESPACE)
  updateScrubberTime(time) {
    if (isNotNumber(time)) {
      throw new Error('Cannot update scrubber time with non-number');
    }

    return {
      time,
    };
  },

  /**
   * Update the media dimensions of the meeia in the trimmer modal
   *
   * @param {Number} width - the width of the media being trimmed
   * @param {Number} height - the height of the media being trimmed
   * @returns {{width: Number, height: Number}}
   */
  @actionCreator(REDUX_NAMESPACE)
  updateMediaDimensions(width, height) {
    if (isNotNumber(width) || isNotNumber(height)) {
      throw new Error('Cannot update dimensions to a non number value');
    }

    return {
      height,
      width,
    };
  },
};

export default ACTION_CREATORS;
